import './command-bar.css';

import { Dialog } from '@mui/material';
import { Command } from 'cmdk';
import { useCallback, useEffect, useRef } from 'react';

import { useCommandBarEmptyMessages, useCommandBarPlaceholder, useCommandBarProviders } from './command-bar-context.js';
import { useCommandBarStore } from './command-bar-store.js';

/**
 * The global command bar component.
 * Renders a cmdk command palette in a MUI Dialog.
 *
 * Must be rendered inside a CommandBarProvider.
 */
export function GlobalCommandBar() {
	const isOpen = useCommandBarStore((s) => s.isOpen);
	const search = useCommandBarStore((s) => s.search);
	const setSearch = useCommandBarStore((s) => s.setSearch);
	const close = useCommandBarStore((s) => s.close);
	const addToHistory = useCommandBarStore((s) => s.addToHistory);
	const navigateHistoryUp = useCommandBarStore((s) => s.navigateHistoryUp);
	const navigateHistoryDown = useCommandBarStore((s) => s.navigateHistoryDown);
	const historyIndex = useCommandBarStore((s) => s.historyIndex);

	const placeholder = useCommandBarPlaceholder();
	const providers = useCommandBarProviders(search);
	const emptyMessages = useCommandBarEmptyMessages();

	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	// Select all text when dialog opens
	useEffect(() => {
		if (isOpen && inputRef.current) {
			requestAnimationFrame(() => {
				inputRef.current?.select();
			});
		}
	}, [isOpen]);

	// Handle close with history tracking
	const handleClose = useCallback(() => {
		if (search.trim()) {
			addToHistory(search);
		}
		close();
	}, [search, addToHistory, close]);

	// Handle up arrow for history navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			// Always refocus the input when the user presses a key to maintain focus
			// Above other elements (selected datagrid elements like to keep focus)
			inputRef.current?.focus();
			if (e.key === 'ArrowUp') {
				// Check if we're at the top of the list (first item selected or no items)
				const selectedItem = listRef.current?.querySelector('[data-selected="true"]');
				const firstItem = listRef.current?.querySelector('[cmdk-item]');

				// If we're already navigating history OR first item is selected, navigate history
				if (historyIndex >= 0 || selectedItem === firstItem || !firstItem) {
					e.preventDefault();
					const historyQuery = navigateHistoryUp();
					if (historyQuery !== null) {
						setSearch(historyQuery);
					}
				}
			} else if (e.key === 'ArrowDown' && historyIndex >= 0) {
				// If navigating history, down arrow goes back through history
				e.preventDefault();
				const historyQuery = navigateHistoryDown();
				if (historyQuery !== null) {
					setSearch(historyQuery);
				}
			} else if (e.key === 'Enter') {
				// Select the first item if it's a command item and there's no selected item
				const firstItem =
					listRef.current?.querySelector('[data-selected="true"]') ?? listRef.current?.querySelector('[cmdk-item]');
				if (firstItem) {
					(firstItem as HTMLElement).click();
					e.preventDefault();
				}
			}
		},
		[historyIndex, navigateHistoryUp, navigateHistoryDown, setSearch],
	);

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth onKeyDown={handleKeyDown}>
			<Command label='Command Menu' value={search}>
				<Command.Input ref={inputRef} autoFocus placeholder={placeholder} onValueChange={setSearch} />
				<Command.List ref={listRef}>
					{/* Render all registered providers sorted by relevance */}
					{providers.map((provider) => (
						<provider.component {...provider.props} key={provider.id} search={search} onClose={handleClose} />
					))}

					<Command.Empty>
						{emptyMessages.map((message, i) => (
							<span key={i}>{message}</span>
						))}
					</Command.Empty>
				</Command.List>
			</Command>
		</Dialog>
	);
}
